import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import './AvailabilityActions.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AvailabilityActions - Dropdown menu for seller to manage ad availability
 * 
 * Props:
 * - adId: string
 * - currentStatus: 'available' | 'reserved' | 'sold' | 'expired' | 'unavailable' | 'hidden'
 * - onStatusChange: (newStatus) => void
 * - trigger: React node (button that opens menu)
 */
export function AvailabilityActions({ 
  adId, 
  currentStatus = 'available', 
  onStatusChange,
  trigger
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // 'sold' | 'hide' | null
  
  const token = localStorage.getItem('token');
  
  const handleAction = async (action, duration = null) => {
    if (!token) {
      toast.error(t('pleaseLogin') || 'Please log in');
      return;
    }
    
    setLoading(true);
    try {
      let endpoint = `${API_URL}/api/ads/${adId}`;
      let body = {};
      
      switch (action) {
        case 'mark-sold':
          endpoint += '/mark-sold';
          break;
        case 'mark-reserved':
          endpoint += '/mark-reserved';
          if (duration) {
            endpoint += `?duration_hours=${duration}`;
          }
          break;
        case 'mark-available':
          endpoint += '/mark-available';
          break;
        case 'renew':
          endpoint += '/renew';
          break;
        case 'hide':
          endpoint += '/hide';
          break;
        default:
          return;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Show success toast
        switch (action) {
          case 'mark-sold':
            toast.success(t('availability.marked_sold'));
            break;
          case 'mark-reserved':
            toast.success(t('availability.marked_reserved'));
            break;
          case 'mark-available':
            toast.success(t('availability.marked_available'));
            break;
          case 'renew':
            toast.success(t('availability.renewed_success'));
            break;
          case 'hide':
            toast.success(t('availability.hidden') || 'Ad hidden');
            break;
        }
        
        // Callback to parent
        if (onStatusChange) {
          onStatusChange(data.availability_status || action.replace('mark-', ''));
        }
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Action failed');
      }
    } catch (err) {
      console.error('Availability action error:', err);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
      setConfirmDialog(null);
    }
  };
  
  const isDisabled = (action) => {
    if (loading) return true;
    switch (action) {
      case 'mark-sold':
        return currentStatus === 'sold';
      case 'mark-available':
        return currentStatus === 'available';
      case 'renew':
        return currentStatus === 'sold';
      case 'hide':
        return currentStatus === 'hidden';
      default:
        return false;
    }
  };
  
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={loading}>
          {trigger || (
            <button className="availability-actions-trigger" data-testid="availability-menu-trigger">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="1"/>
                <circle cx="12" cy="5" r="1"/>
                <circle cx="12" cy="19" r="1"/>
              </svg>
            </button>
          )}
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="end" className="availability-menu-content">
          {/* Mark as Available (if not already) */}
          {currentStatus !== 'available' && (
            <DropdownMenuItem 
              onClick={() => handleAction('mark-available')}
              disabled={isDisabled('mark-available')}
              className="menu-item-available"
              data-testid="action-mark-available"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>{t('availability.mark_available')}</span>
            </DropdownMenuItem>
          )}
          
          {/* Renew */}
          <DropdownMenuItem 
            onClick={() => handleAction('renew')}
            disabled={isDisabled('renew')}
            className="menu-item-renew"
            data-testid="action-renew"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
            <span>{t('availability.renew')}</span>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          {/* Mark as Reserved (submenu for duration) */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger 
              disabled={currentStatus === 'reserved'}
              className="menu-item-reserved"
              data-testid="action-mark-reserved"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>{t('availability.mark_reserved')}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={() => handleAction('mark-reserved', 24)} data-testid="reserve-24h">
                {t('availability.duration_24h')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('mark-reserved', 48)} data-testid="reserve-48h">
                {t('availability.duration_48h')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('mark-reserved', 168)} data-testid="reserve-7d">
                {t('availability.duration_7d')}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          
          {/* Mark as Sold */}
          <DropdownMenuItem 
            onClick={() => setConfirmDialog('sold')}
            disabled={isDisabled('mark-sold')}
            className="menu-item-sold"
            data-testid="action-mark-sold"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>{t('availability.mark_sold')}</span>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          {/* Hide */}
          <DropdownMenuItem 
            onClick={() => setConfirmDialog('hide')}
            disabled={isDisabled('hide')}
            className="menu-item-hide"
            data-testid="action-hide"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
            <span>{t('availability.hide')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* Confirmation Dialogs */}
      <AlertDialog open={confirmDialog === 'sold'} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('availability.mark_sold')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('availability.confirm_mark_sold')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleAction('mark-sold')}>
              {t('availability.mark_sold')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <AlertDialog open={confirmDialog === 'hide'} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('availability.hide')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('availability.confirm_hide')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleAction('hide')}>
              {t('availability.hide')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default AvailabilityActions;
